import { Component, EventEmitter, Input, Output } from '@angular/core';
import { QuickViewModalShellComponent } from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { AppointmentQuickViewModalComponent } from '../../appointments/components/appointment-quick-view-modal.component';
import { SpecialistDowntimeItem } from '../data-access/specialist-downtime.models';

@Component({
  selector: 'app-specialist-downtime-quick-view-modal',
  standalone: true,
  imports: [QuickViewModalShellComponent, TranslatePipe, AppointmentQuickViewModalComponent],
  template: `
    <lib-quick-view-modal-shell
      [isOpen]="isOpen && !!item"
      title="Specialist Downtime Quick View"
      subtitle="Operational downtime context without leaving the current workflow."
      width="980px"
      (close)="close.emit()"
    >
      @if (item) {
        <div class="quick-view-grid">
          <div class="quick-view-field">
            <span>{{ 'Downtime' | t }}</span>
            <strong>{{ readableId(item.id) }}</strong>
            <div class="quick-view-id-meta">{{ item.id }}</div>
          </div>

          <div class="quick-view-field">
            <span>{{ 'common.status' | t }}</span>
            <strong>{{ statusLabel(item.status) }}</strong>
          </div>

          <div
            class="quick-view-field quick-view-overdue"
            [class.quick-view-overdue--active]="item.isOverdue"
          >
            <span>Overdue</span>
            <strong>{{ overdueLabel(item) }}</strong>
            <div class="quick-view-id-meta">
              {{ item.isOverdue ? 'Finish downtime manually when the specialist is back' : 'Expected window is not overdue' }}
            </div>
          </div>

          <div
            class="quick-view-field quick-view-resolution"
            [class.quick-view-resolution--resolved]="isImpactResolved(item)"
            [class.quick-view-resolution--action-required]="!isImpactResolved(item)"
          >
            <span>Impact Status</span>
            <strong>{{ impactStatusLabel(item) }}</strong>
            <div class="quick-view-id-meta">{{ impactStatusSummary(item) }}</div>
          </div>

          <div class="quick-view-field quick-view-field--wide quick-view-impact-overview">
            <span>Impact Workload</span>
            <div class="quick-view-impact-overview__cards">
              <article class="quick-view-impact-bucket quick-view-impact-bucket--automatic">
                <div>
                  <span>Automatic channel</span>
                  <strong>{{ item.reachabilityImpact?.automaticallyReachableClientsCount ?? 0 }}</strong>
                </div>
                <p>
                  Email {{ item.reachabilityImpact?.automaticEmailClientsCount ?? 0 }}
                  · Telegram {{ item.reachabilityImpact?.automaticTelegramClientsCount ?? 0 }}
                  · Pending {{ item.impactResolution?.automaticPendingCount ?? 0 }}
                  · Failed {{ item.impactResolution?.automaticFailedCount ?? 0 }}
                </p>
              </article>

              <article class="quick-view-impact-bucket quick-view-impact-bucket--manual">
                <div>
                  <span>Manual channel</span>
                  <strong>{{ item.reachabilityImpact?.manualFollowUpClientsCount ?? 0 }}</strong>
                </div>
                <p>
                  Phone only {{ item.reachabilityImpact?.manualPhoneOnlyClientsCount ?? 0 }}
                  · Pending {{ item.impactResolution?.manualPendingCount ?? 0 }}
                </p>
              </article>

              <article class="quick-view-impact-bucket quick-view-impact-bucket--no-channel">
                <div>
                  <span>No channel</span>
                  <strong>{{ item.reachabilityImpact?.noChannelClientsCount ?? 0 }}</strong>
                </div>
                <p>Pending {{ item.impactResolution?.noChannelPendingCount ?? 0 }}</p>
              </article>
            </div>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Clinic' | t }}</span>
            <strong>{{ clinicLabel(item) }}</strong>
            <div class="quick-view-id-meta">{{ item.clinicId }}</div>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Specialist' | t }}</span>
            <strong>{{ specialistLabel(item) }}</strong>
            <div class="quick-view-id-meta">{{ item.specialistUserId }}</div>
          </div>

          <div class="quick-view-field">
            <span>Downtime Window</span>
            <strong>{{ formatWindow(item.startTime, item.expectedRepairUntil, item.clinicTimeZone) }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Actual End Time' | t }}</span>
            <strong>{{ formatAsLocalNaiveDateTime(item.actualEndTime, item.clinicTimeZone) }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Affected Appointments' | t }}</span>
            <strong>{{ item.affectedAppointmentIds.length }}</strong>
            @if (item.affectedAppointmentIds.length > 0) {
              <div class="quick-view-id-meta">{{ item.affectedAppointmentIds.join(', ') }}</div>
            }
          </div>

          @if (item.impactItems.length > 0) {
            <div class="quick-view-field quick-view-field--wide quick-view-impact-items">
              <span>Affected Appointment State</span>
              <div class="quick-view-impact-list">
                @for (impactItem of item.impactItems; track impactItem.appointmentId) {
                  <article class="quick-view-impact-item">
                    <div class="quick-view-impact-item__head">
                      <strong>{{ impactItemClientLabel(impactItem) }}</strong>
                      <span
                        class="quick-view-impact-item__badge"
                        [class.quick-view-impact-item__badge--warning]="impactItem.actual.appointmentStatus === 'reschedule_required'"
                        [class.quick-view-impact-item__badge--resolved]="impactItem.actual.appointmentStatus === 'confirmed'"
                      >
                        {{ statusLabelText(impactItem.actual.appointmentStatus || 'missing') }}
                      </span>
                    </div>
                    <div class="quick-view-impact-item__channels">
                      <span
                        class="quick-view-impact-item__channel"
                        [class.quick-view-impact-item__channel--manual]="impactItem.currentReachability.reachabilityClass === 'manual'"
                        [class.quick-view-impact-item__channel--no-channel]="impactItem.currentReachability.reachabilityClass === 'no_channel'"
                      >
                        {{ reachabilityLabel(impactItem.currentReachability.reachabilityClass) }}
                      </span>
                      <span>{{ contactSummary(impactItem) }}</span>
                      @if (impactItem.currentReachability.changedFromSnapshot) {
                        <span class="quick-view-impact-item__drift">Reachability changed</span>
                      }
                    </div>
                    <div class="quick-view-impact-item__meta">
                      <span>Snapshot {{ impactItem.snapshot.appointmentStatus }} · {{ formatImpactWindow(impactItem.snapshot.appointmentStart, impactItem.snapshot.appointmentEnd, item.clinicTimeZone) }}</span>
                      <span>Actual {{ formatImpactWindow(impactItem.actual.appointmentStart, impactItem.actual.appointmentEnd, item.clinicTimeZone) }}</span>
                    </div>
                    <div class="quick-view-impact-item__meta">
                      <span>{{ proposalSummary(impactItem, item.clinicTimeZone) }}</span>
                      <span>Notifications {{ notificationSummary(impactItem) }}</span>
                      <span>{{ manualFollowUpSummary(impactItem, item.clinicTimeZone) }}</span>
                    </div>
                    <div class="quick-view-impact-item__footer">
                      <div class="quick-view-id-meta">{{ impactItem.appointmentId }}</div>
                      <button
                        type="button"
                        class="quick-view-impact-item__link"
                        (click)="openAppointmentQuickView(impactItem.appointmentId)"
                      >
                        Open appointment
                      </button>
                    </div>
                  </article>
                }
              </div>
            </div>
          }

          <div class="quick-view-field">
            <span>Affected Clients</span>
            <strong>{{ item.reachabilityImpact?.affectedClientsCount ?? 0 }}</strong>
          </div>

          <div class="quick-view-field">
            <span>Automatic Reachable</span>
            <strong>{{ item.reachabilityImpact?.automaticallyReachableClientsCount ?? 0 }}</strong>
            <div class="quick-view-id-meta">
              Email {{ item.reachabilityImpact?.automaticEmailClientsCount ?? 0 }}
              · Telegram {{ item.reachabilityImpact?.automaticTelegramClientsCount ?? 0 }}
            </div>
          </div>

          <div class="quick-view-field">
            <span>Manual Follow-Up</span>
            <strong>{{ item.reachabilityImpact?.manualFollowUpClientsCount ?? 0 }}</strong>
            <div class="quick-view-id-meta">
              Phone only {{ item.reachabilityImpact?.manualPhoneOnlyClientsCount ?? 0 }}
            </div>
          </div>

          <div class="quick-view-field">
            <span>No Channel</span>
            <strong>{{ item.reachabilityImpact?.noChannelClientsCount ?? 0 }}</strong>
          </div>

          <div class="quick-view-field">
            <span>Timezone</span>
            <strong>{{ item.clinicTimeZone || '—' }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Created At' | t }}</span>
            <strong>{{ formatAsLocalNaiveDateTime(item.createdAt, item.clinicTimeZone) }}</strong>
          </div>

          <div class="quick-view-field">
            <span>{{ 'Updated At' | t }}</span>
            <strong>{{ formatAsLocalNaiveDateTime(item.updatedAt, item.clinicTimeZone) }}</strong>
          </div>

          <div class="quick-view-field quick-view-field--wide">
            <span>{{ 'Reason' | t }}</span>
            <strong>{{ item.reason || '—' }}</strong>
          </div>
        </div>
      }
    </lib-quick-view-modal-shell>

    <app-appointment-quick-view-modal
      [isOpen]="isAppointmentQuickViewOpen"
      [appointmentId]="selectedAppointmentId"
      title="Affected Appointment"
      (close)="closeAppointmentQuickView()"
    />
  `,
  styles: [`
    .quick-view-resolution--resolved {
      border-color: rgba(34, 197, 94, 0.38);
      background: rgba(34, 197, 94, 0.08);
    }

    .quick-view-resolution--resolved strong {
      color: #166534;
    }

    .quick-view-resolution--action-required {
      border-color: rgba(220, 38, 38, 0.36);
      background: rgba(220, 38, 38, 0.08);
    }

    .quick-view-resolution--action-required strong {
      color: #991b1b;
    }

    .quick-view-overdue--active {
      border-color: rgba(220, 38, 38, 0.36);
      background: rgba(220, 38, 38, 0.08);
    }

    .quick-view-overdue--active strong {
      color: #991b1b;
    }

    .quick-view-impact-overview {
      display: grid;
      gap: 10px;
    }

    .quick-view-impact-overview__cards {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 10px;
      width: 100%;
    }

    .quick-view-impact-bucket {
      display: grid;
      gap: 8px;
      min-height: 96px;
      border: 1px solid #dbe4f0;
      border-radius: 10px;
      background: #ffffff;
      padding: 12px;
    }

    .quick-view-impact-bucket div {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: baseline;
    }

    .quick-view-impact-bucket span {
      color: #475569;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .quick-view-impact-bucket strong {
      font-size: 24px;
      line-height: 1;
    }

    .quick-view-impact-bucket p {
      margin: 0;
      color: #475569;
      font-size: 12px;
      line-height: 1.45;
    }

    .quick-view-impact-bucket--automatic {
      border-color: rgba(37, 99, 235, 0.28);
    }

    .quick-view-impact-bucket--manual {
      border-color: rgba(217, 119, 6, 0.32);
    }

    .quick-view-impact-bucket--no-channel {
      border-color: rgba(220, 38, 38, 0.28);
    }

    .quick-view-impact-items {
      display: grid;
      gap: 10px;
    }

    .quick-view-impact-list {
      display: grid;
      gap: 10px;
      width: 100%;
    }

    .quick-view-impact-item {
      display: grid;
      gap: 8px;
      padding: 12px;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      background: #ffffff;
    }

    .quick-view-impact-item__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .quick-view-impact-item__badge {
      display: inline-flex;
      align-items: center;
      min-height: 24px;
      border-radius: 999px;
      background: #dcfce7;
      color: #166534;
      padding: 0 8px;
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
    }

    .quick-view-impact-item__badge--warning {
      background: #fef3c7;
      color: #92400e;
    }

    .quick-view-impact-item__badge--resolved {
      background: #dcfce7;
      color: #166534;
    }

    .quick-view-impact-item__channels {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      color: #475569;
      font-size: 12px;
      line-height: 1.4;
    }

    .quick-view-impact-item__channel {
      display: inline-flex;
      align-items: center;
      min-height: 22px;
      border-radius: 999px;
      background: #dbeafe;
      color: #1d4ed8;
      padding: 0 8px;
      font-weight: 700;
      white-space: nowrap;
    }

    .quick-view-impact-item__channel--manual {
      background: #fef3c7;
      color: #92400e;
    }

    .quick-view-impact-item__channel--no-channel {
      background: #fee2e2;
      color: #991b1b;
    }

    .quick-view-impact-item__meta {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      color: #475569;
      font-size: 12px;
      line-height: 1.4;
    }

    .quick-view-impact-item__drift {
      color: #b45309;
      font-weight: 700;
    }

    .quick-view-impact-item__footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-width: 0;
    }

    .quick-view-impact-item__footer .quick-view-id-meta {
      min-width: 0;
      overflow-wrap: anywhere;
    }

    .quick-view-impact-item__link {
      flex: 0 0 auto;
      min-height: 32px;
      border: 1px solid #bfdbfe;
      border-radius: 7px;
      background: #eff6ff;
      color: #1d4ed8;
      cursor: pointer;
      font: inherit;
      font-size: 12px;
      font-weight: 700;
      padding: 0 10px;
      white-space: nowrap;
    }

    .quick-view-impact-item__link:hover {
      border-color: #93c5fd;
      background: #dbeafe;
    }

    @media (max-width: 760px) {
      .quick-view-impact-overview__cards {
        grid-template-columns: 1fr;
      }

      .quick-view-impact-item__head {
        align-items: flex-start;
        flex-direction: column;
      }

      .quick-view-impact-item__footer {
        align-items: flex-start;
        flex-direction: column;
      }
    }
  `],
})
export class SpecialistDowntimeQuickViewModalComponent {
  @Input() public isOpen = false;
  @Input() public item: SpecialistDowntimeItem | null = null;
  @Input() public clinics: Array<{ id: string; label: string }> = [];
  @Input() public specialists: Array<{ id: string; label: string }> = [];

  @Output() public readonly close = new EventEmitter<void>();

  protected isAppointmentQuickViewOpen = false;
  protected selectedAppointmentId: string | null = null;

  protected formatAsLocalNaiveDateTime(value: string | null, timeZone?: string | null): string {
    if (!value) {
      return '—';
    }

    const normalized = value.trim();
    if (/[zZ]|[+-]\d{2}:\d{2}$/.test(normalized)) {
      const date = new Date(normalized);
      if (!Number.isNaN(date.getTime())) {
        const formatted = this.formatDateInTimeZone(date, timeZone);
        if (formatted) {
          return formatted;
        }
      }
    }

    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) {
      return normalized;
    }

    const [, year, month, day, hours, minutes] = match;
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  protected formatWindow(startTime: string | null, expectedRepairUntil: string | null, timeZone?: string | null): string {
    return `${this.formatAsLocalNaiveDateTime(startTime, timeZone)} -> ${this.formatAsLocalNaiveDateTime(expectedRepairUntil, timeZone)}`;
  }

  protected clinicLabel(item: SpecialistDowntimeItem): string {
    return item.clinicName?.trim()
      || this.clinics.find((clinic) => clinic.id === item.clinicId)?.label
      || this.readableId(item.clinicId);
  }

  protected specialistLabel(item: SpecialistDowntimeItem): string {
    return item.specialistLabel?.trim()
      || this.specialists.find((specialist) => specialist.id === item.specialistUserId)?.label
      || this.readableId(item.specialistUserId);
  }

  protected statusLabel(status: SpecialistDowntimeItem['status']): string {
    switch (status) {
      case 'pending_approval':
        return 'Pending approval';
      case 'active':
        return 'Active';
      case 'resolved':
        return 'Resolved';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  }

  protected isImpactResolved(item: SpecialistDowntimeItem): boolean {
    return item.impactResolution?.status === 'resolved';
  }

  protected impactStatusLabel(item: SpecialistDowntimeItem): string {
    return this.isImpactResolved(item) ? 'Resolved' : 'Action required';
  }

  protected impactStatusSummary(item: SpecialistDowntimeItem): string {
    const resolution = item.impactResolution;
    if (!resolution) {
      return 'No impact resolution data';
    }

    if (resolution.status === 'resolved') {
      if (item.reachabilityImpact?.affectedAppointmentsCount === 0) {
        return 'No affected appointments';
      }
      return 'All affected items are resolved';
    }

    const parts = [
      `${resolution.unresolvedCount} unresolved`,
      resolution.pendingAppointmentCount > 0 ? `${resolution.pendingAppointmentCount} appointments pending` : null,
      resolution.automaticPendingCount > 0 ? `${resolution.automaticPendingCount} automatic pending` : null,
      resolution.automaticFailedCount > 0 ? `${resolution.automaticFailedCount} automatic failed` : null,
      resolution.manualPendingCount > 0 ? `${resolution.manualPendingCount} manual follow-up` : null,
      resolution.noChannelPendingCount > 0 ? `${resolution.noChannelPendingCount} no channel` : null,
    ].filter((part): part is string => !!part);

    return parts.join(' · ');
  }

  protected overdueLabel(item: SpecialistDowntimeItem): string {
    const minutes = item.overdueByMinutes ?? 0;
    if (!item.isOverdue || minutes <= 0) {
      return 'No';
    }

    if (minutes < 60) {
      return `${minutes} min`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  }

  protected impactItemClientLabel(item: SpecialistDowntimeItem['impactItems'][number]): string {
    return item.snapshot.clientDisplayName
      || item.snapshot.clientEmail
      || item.snapshot.clientPhone
      || this.readableId(item.clientId);
  }

  protected statusLabelText(status: string): string {
    return status
      .split('_')
      .map((part) => part ? `${part[0].toUpperCase()}${part.slice(1)}` : part)
      .join(' ');
  }

  protected formatImpactWindow(
    start: string | null,
    end: string | null,
    timeZone?: string | null,
  ): string {
    return `${this.formatAsLocalNaiveDateTime(start, timeZone)} -> ${this.formatAsLocalNaiveDateTime(end, timeZone)}`;
  }

  protected notificationSummary(item: SpecialistDowntimeItem['impactItems'][number]): string {
    const state = item.notificationState;
    return `sent ${state.sentCount} / pending ${state.pendingCount} / failed ${state.failedCount}`;
  }

  protected manualFollowUpLabel(item: SpecialistDowntimeItem['impactItems'][number]): string {
    return this.statusLabelText(item.manualFollowUp.status);
  }

  protected reachabilityLabel(
    reachabilityClass: SpecialistDowntimeItem['impactItems'][number]['currentReachability']['reachabilityClass'],
  ): string {
    switch (reachabilityClass) {
      case 'automatic':
        return 'Automatic';
      case 'manual':
        return 'Manual';
      case 'no_channel':
        return 'No channel';
      default:
        return this.statusLabelText(reachabilityClass);
    }
  }

  protected contactSummary(item: SpecialistDowntimeItem['impactItems'][number]): string {
    const parts = [
      item.currentReachability.hasEmail ? 'Email' : null,
      item.currentReachability.hasActiveTelegram ? 'Telegram' : null,
      item.currentReachability.hasPhone ? 'Phone' : null,
    ].filter((part): part is string => !!part);

    return parts.length > 0 ? parts.join(' · ') : 'No contact channel';
  }

  protected proposalSummary(
    item: SpecialistDowntimeItem['impactItems'][number],
    timeZone?: string | null,
  ): string {
    const proposal = item.latestRescheduleProposal;
    if (!proposal) {
      return 'Proposal —';
    }

    return `Proposal ${this.statusLabelText(proposal.status)} · ${this.formatImpactWindow(proposal.oldStartTime, proposal.oldEndTime, timeZone)} -> ${this.formatImpactWindow(proposal.newStartTime, proposal.newEndTime, timeZone)} · ${this.readableId(proposal.id)}`;
  }

  protected manualFollowUpSummary(
    item: SpecialistDowntimeItem['impactItems'][number],
    timeZone?: string | null,
  ): string {
    const manual = item.manualFollowUp;
    if (manual.status === 'not_required') {
      return 'Manual not required';
    }

    const updatedAt = manual.updatedAt
      ? ` · ${this.formatAsLocalNaiveDateTime(manual.updatedAt, timeZone)}`
      : '';
    const note = manual.note ? ` · ${manual.note}` : '';

    return `Manual ${this.manualFollowUpLabel(item)}${updatedAt}${note}`;
  }

  protected openAppointmentQuickView(appointmentId: string): void {
    const normalized = appointmentId.trim();
    if (!normalized) {
      return;
    }

    this.selectedAppointmentId = normalized;
    this.isAppointmentQuickViewOpen = true;
  }

  protected closeAppointmentQuickView(): void {
    this.isAppointmentQuickViewOpen = false;
    this.selectedAppointmentId = null;
  }

  protected readableId(value: string | null | undefined): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '—';
    }

    if (normalized.length > 18) {
      return `${normalized.slice(0, 8)}...${normalized.slice(-6)}`;
    }

    return normalized;
  }

  private formatDateInTimeZone(date: Date, timeZone?: string | null): string | null {
    try {
      const formatter = new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timeZone?.trim() || undefined,
      });
      const parts = formatter.formatToParts(date);
      const value = (type: string): string =>
        parts.find((part) => part.type === type)?.value ?? '';

      return `${value('day')}.${value('month')}.${value('year')} ${value('hour')}:${value('minute')}`;
    } catch {
      return null;
    }
  }
}
